import {
  AppBar,
  Button,
  Collapse,
  Container,
  Drawer,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Popover,
  Toolbar,
  useMediaQuery,
} from '@mui/material';
import clsx from 'clsx';
import GoLogoSvg from 'common/GoLogoSvg';
import {
  AppDomainEnum,
  MediaQueryBreakpointEnum,
  UserTypeEnum,
} from 'constants/Global';
import { RouteEnum } from 'constants/RouteConstants';
import usePopover from 'hooks/usePopover';
import useResizeObserver from 'hooks/useResizeObserver';
import useToggle from 'hooks/useToggle';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

function PublicHeader(props) {
  const { position, ...restProps } = props;
  const ismd = useMediaQuery(MediaQueryBreakpointEnum.md);
  const islg = useMediaQuery(MediaQueryBreakpointEnum.lg);

  const [searchParams] = useSearchParams();

  const view = searchParams.get('view');

  const [isDrawer, toggleDrawer] = useToggle();

  const headerRef = useRef(/** @type {HTMLElement}*/ (null));
  const [headerRect, setHeaderRect] = useState(defaultHeaderRect);

  const observer = useResizeObserver(() => {
    if (headerRef.current) {
      setHeaderRect({
        width: headerRef.current.offsetWidth,
        height: headerRef.current.offsetHeight,
      });
    }
  });

  useEffect(() => {
    const node = headerRef.current;
    if (node) {
      observer.observe(node);
      return () => {
        observer.unobserve(node);
      };
    }
  }, [observer]);

  if (view === 'mobile-app') {
    return null;
  }

  const logo = (
    <div>
      <a href={AppDomainEnum.DEFAULT}>
        <GoLogoSvg className='w-28 h-16' variant={2} />
      </a>
    </div>
  );

  // const loginButton = (
  //   <Button
  //     variant='text'
  //     color='inherit'
  //     href={AppDomainEnum.RIDE.concat(RouteEnum.LOGIN)}
  //     size='large'
  //     borderradius='circular'
  //   >
  //     LOGIN
  //   </Button>
  // );

  const becomeARiderButton = (
    <Button
      borderradius='circular'
      size='large'
      color='secondary'
      href={AppDomainEnum.RIDE.concat(
        RouteEnum.SIGNUP,
        '?accountType=',
        UserTypeEnum.DRIVER
      )}
    >
      BECOME A DRIVER
    </Button>
  );

  return (
    <>
      <AppBar
        sx={{ backgroundColor: 'inherit' }}
        className=' w-full'
        {...{ ref: headerRef, position, ...restProps }}
      >
        <Container className='bg-[#F8F8F8] text-black'>
          <Toolbar disableGutters className='items-center gap-2'>
            {logo}
            <div className='flex-1' />
            {islg && (
              <>
                <List dense className='flex'>
                  {LINKS.map((link, index) => (
                    <PublicPageHeaderDesktopLink
                      {...{ key: index, link, index }}
                    />
                  ))}
                </List>
                <div className='flex-1' />
              </>
            )}
            {ismd && (
              <>
                {/* {loginButton} */}
                {becomeARiderButton}
              </>
            )}
            {!islg && (
              <IconButton color='inherit' onClick={toggleDrawer}>
                <Icon>menu</Icon>
              </IconButton>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {['fixed', 'absolute'].includes(position) && <div style={headerRect} />}
      {!islg && (
        <Drawer
          variant='temporary'
          open={isDrawer}
          onClose={toggleDrawer}
          anchor='top'
          PaperProps={{ className: 'p-4 bg-gray-50 rounded-b-3xl' }}
        >
          <div className='mb-4 flex items-center justify-between'>
            {logo}
            <IconButton aria-label='close' onClick={toggleDrawer}>
              <Icon>close</Icon>
            </IconButton>
          </div>
          <List className='mb-8'>
            {LINKS.map((link, index) => (
              <PublicPageHeaderMobileLink
                {...{ key: index, link, index, toggleDrawer }}
              />
            ))}
          </List>
          <div className='grid grid-cols-1 gap-2'>
            {becomeARiderButton}
            {/* {loginButton} */}
          </div>
        </Drawer>
      )}
    </>
  );
}

PublicHeader.defaultProps = {
  position: 'fixed',
};

export default PublicHeader;

const defaultHeaderRect = { width: 0, height: 0 };

function PublicPageHeaderDesktopLink({ link, index }) {
  const { label, to, href, children } = link;

  const isGroup = !!children;

  const childrenPopover = usePopover();

  return (
    <>
      <ListItem
        key={index}
        className={clsx('rounded w-auto hover:bg-primary-lighter')}
        classes={{
          selected: 'bg-inherit text-secondary-yellow',
        }}
        {...(isGroup
          ? {
              onClick: childrenPopover.togglePopover,
              // onMouseEnter: childrenPopover.togglePopover,
              // onMouseLeave: () => childrenPopover.togglePopover(),
            }
          : href
          ? {
              component: 'a',
              href,
              selected: new URL(href).href === window.location.href,
            }
          : { component: Link, to })}
      >
        <ListItemText primary={label} classes={{ primary: 'font-bold' }} />
        {isGroup && (
          <>
            <Icon>
              {childrenPopover.isOpen ? 'expand_less' : 'expand_more'}
            </Icon>
          </>
        )}
      </ListItem>

      {isGroup && (
        <Popover
          open={childrenPopover.isOpen}
          anchorEl={childrenPopover.anchorEl}
          onClose={childrenPopover.togglePopover}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          // PaperProps={{ className: "w-36" }}
        >
          {children?.map(
            (
              {
                icon,
                children,
                label,
                description,
                onClick,
                href,
                to,
                ...rest
              },
              index
            ) => (
              <ListItem
                key={index}
                className=''
                onClick={() => {
                  childrenPopover.togglePopover();
                  onClick?.();
                }}
                {...(href ? { component: 'a', href } : { component: Link, to })}
                {...rest}
              >
                <ListItemText
                  primary={label}
                  secondary={description}
                  classes={{ primary: 'font-semibold' }}
                />
              </ListItem>
            )
          )}
        </Popover>
      )}
    </>
  );
}

function PublicPageHeaderMobileLink({ link, index, toggleDrawer }) {
  const { label, to, href, children } = link;

  const isGroup = !!children;

  // const location = useLocation();

  // const match = useMemo(() => {
  //   let result = null;
  //   if (children) {
  //     for (const child of children) {
  //       result = matchPath({ path: child.to + '/*' }, location.pathname);
  //       if (result) {
  //         return result;
  //       }
  //     }
  //     return result;
  //   }
  // }, [children, location.pathname]);

  // const match = useMatch(to || "/");

  const [isSubMenu, toggleSubMenu] = useToggle();

  return (
    <>
      <ListItem
        key={index}
        className={clsx('rounded w-auto hover:bg-primary-lighter')}
        classes={{ selected: 'bg-inherit text-secondary-yellow' }}
        {...(isGroup
          ? { onClick: toggleSubMenu }
          : href
          ? {
              component: 'a',
              href,
              selected: new URL(href).href === window.location.href,
            }
          : { component: Link, to, onClick: toggleDrawer })}
      >
        <ListItemText
          primary={label}
          classes={{ primary: 'text-center font-bold' }}
        />
        {isGroup && (
          <ListItemSecondaryAction>
            <Icon>{isSubMenu ? 'expand_less' : 'expand_more'}</Icon>
          </ListItemSecondaryAction>
        )}
      </ListItem>
      {isGroup && (
        <Collapse in={isSubMenu} timeout='auto' unmountOnExit>
          <List component='div' disablePadding>
            {children?.map(
              (
                {
                  icon,
                  children,
                  label,
                  description,
                  onClick,
                  to,
                  href,
                  ...rest
                },
                index
              ) => (
                <ListItem
                  key={index}
                  className='px-6'
                  onClick={() => {
                    toggleSubMenu();
                    onClick?.();
                    toggleDrawer();
                  }}
                  {...(href
                    ? { component: 'a', href }
                    : { component: Link, to })}
                  {...rest}
                >
                  <ListItemText
                    primary={label}
                    secondary={description}
                    classes={{ primary: 'font-semibold' }}
                  />
                </ListItem>
              )
            )}
          </List>
        </Collapse>
      )}
    </>
  );
}

const LINKS = [
  { label: 'GoRide', href: AppDomainEnum.RIDE },
  // { label: 'DRIVE', href: AppDomainEnum.RIDE },
  { label: 'GoDelivery', href: AppDomainEnum.LOGISTICS },
  { label: 'GoEats', href: AppDomainEnum.SHOP },
  { label: 'Partner', href: AppDomainEnum.DEFAULT.concat(RouteEnum.PARTNER) },
  { label: 'Contact', href: AppDomainEnum.DEFAULT.concat(RouteEnum.CONTACT) },
];
